import type { FC } from 'react';
import React, { useContext } from 'react';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';

import type { PaymentMethodType } from '../../../../services/commonCheckout/types/PaymentMethod';
import {
  ELEMENT_IDS,
  TEXT_CONTENT_IDS,
  TITLE_IDS,
} from '../../../../utils/element-ids';
import { AppContext } from '../../../contextStore/AppContext';
import CallToActionCard from '../../../../components/CallToActionCard/CallToActionCard';
import PaymentMethodDetails from '../../../../components/PaymentMethodButton/PaymentMethodDetails';
import PageTitle from '../../../../ui/PageTitle/PageTitle';
import useFormConfigManager from '../../../../hooks/useFormConfigManager/useFormConfigManager';
import isSecondaryTypeDrawer from '../../../../utils/container-config/isSecondaryTypeDrawer';
import useEnvironmentContextProvider from '../../../contextStore/EnvironmentContext/useEnvironmentContextProvider';
import FormLabelVisuallyHidden from '../../../../ui/FormLabelVisuallyHidden/FormLabelVisuallyHidden';

import type { FormattedPaymentMethod } from './types';
import StyledFormControlLabel from './styled-components/StyledFormControlLabel';
import StyledRadioRoot from './styled-components/StyledRadioRoot';
import StyledRadioGroup from './styled-components/StyledRadioGroup';
import isPaymentMethodNotSelectable from './utils/isPaymentMethodNotSelectable';
import { isManufacturerCardFeatureEnabledForMerchant } from './utils/isManufacturerCardEnabledForMerchant';

export type WalletProps = {
  header?: string;
  formattedPaymentMethods: FormattedPaymentMethod<PaymentMethodType>[];
  onSelectPayment?: (
    paymentMethod: FormattedPaymentMethod<PaymentMethodType>,
  ) => void;
  onManageWalletClick: () => void;
  renderExpressCheckout?: () => React.ReactNode;
};

export const Wallet: FC<WalletProps> = (props) => {
  const {
    header = 'Your wallet',
    formattedPaymentMethods,
    onSelectPayment,
    onManageWalletClick,
    renderExpressCheckout,
  } = props;
  const { selectedPaymentMethodId, initialSessionData } =
    useContext(AppContext);
  const { containerConfig } = useEnvironmentContextProvider();

  const [ariaLiveMessage, setAriaLiveMessage] =
    React.useState('');

  function findDefaultPaymentMethodId() {
    return formattedPaymentMethods.find(
      (formattedPaymentMethod) =>
        formattedPaymentMethod.isDefault,
    )?.id;
  }

  function getSelectedId() {
    // TODO: need to clean up logic of this func
    if (!Array.isArray(formattedPaymentMethods)) {
      return '';
    }

    if (selectedPaymentMethodId) {
      return selectedPaymentMethodId;
    } else if (formattedPaymentMethods.length === 1) {
      return formattedPaymentMethods[0].id;
    }

    return findDefaultPaymentMethodId();
  }

  const { title } = useFormConfigManager({
    title: header,
    titleTestId: TITLE_IDS.MAIN_PAGE_TITLE,
    processFooterConfig: false,
    shouldOverrideFormConfigTitleAndActionsOnForm:
      isSecondaryTypeDrawer(containerConfig),
  });

  const onChangeSelectedPaymentMethod = (
    paymentMethodId: string,
  ) => {
    const matchingPayment = formattedPaymentMethods.find(
      (formattedPaymentMethod) =>
        formattedPaymentMethod.id === paymentMethodId,
    );
    if (onSelectPayment && matchingPayment) {
      onSelectPayment(matchingPayment);
    }
    setAriaLiveMessage(
      matchingPayment ? matchingPayment.ariaDescription : '',
    );
  };

  const isManufacturerCardFunctionalityEnabledForMerchant =
    isManufacturerCardFeatureEnabledForMerchant(
      initialSessionData,
    );
  return (
    <Grid
      container
      direction="column"
    >
      <Grid
        container
        direction="column"
      >
        <Grid>
          <PageTitle
            titleId={TITLE_IDS.MAIN_PAGE_TITLE}
            title={title}
            subTitle={
              formattedPaymentMethods?.length !== 0
                ? 'Select payment method'
                : ''
            }
            subTitleId={TEXT_CONTENT_IDS.WALLET_FORM_TEXT}
          />
        </Grid>
        {renderExpressCheckout ? (
          <Grid>{renderExpressCheckout()}</Grid>
        ) : null}

        <FormControl
          component="fieldset"
          aria-labelledby={TEXT_CONTENT_IDS.WALLET_FORM_TEXT}
          sx={{ width: '100%' }}
        >
          {formattedPaymentMethods?.length !== 0 && (
            <FormLabelVisuallyHidden component="legend">
              Select payment method
            </FormLabelVisuallyHidden>
          )}
          <StyledRadioGroup
            name="payment-method"
            color="secondary"
            value={getSelectedId() ?? null}
            onChange={(event) => {
              onChangeSelectedPaymentMethod(
                event.currentTarget.value,
              );
            }}
          >
            {formattedPaymentMethods.map(
              (formattedPaymentMethod, index) => {
                const paymentInputId = `_ccg_paymentInputId_${formattedPaymentMethod.last4}_${index}`;

                return isPaymentMethodNotSelectable(
                  isManufacturerCardFunctionalityEnabledForMerchant,
                  formattedPaymentMethod,
                ) ? (
                  <StyledFormControlLabel
                    data-testid={`paymentMethod-${index + 1}`}
                    htmlFor={paymentInputId}
                    value={
                      formattedPaymentMethod.paymentMethodId
                    }
                    control={
                      <input
                        className="OneLinkNoTx"
                        type="hidden"
                        aria-label={
                          formattedPaymentMethod.ariaDescription
                        }
                      />
                    }
                    key={formattedPaymentMethod.paymentMethodId}
                    label={
                      <PaymentMethodDetails
                        paymentMethod={formattedPaymentMethod}
                        showCaIcon={false}
                      />
                    }
                    onClick={() => {
                      onChangeSelectedPaymentMethod(
                        formattedPaymentMethod.id,
                      );
                    }}
                  />
                ) : (
                  <StyledFormControlLabel
                    data-testid={`paymentMethod-${index + 1}`}
                    htmlFor={paymentInputId}
                    value={
                      formattedPaymentMethod.paymentMethodId
                    }
                    control={
                      <StyledRadioRoot
                        className="OneLinkNoTx"
                        inputProps={{
                          // @ts-expect-error need to pass down data-testid to inputProps
                          'data-testid': paymentInputId,
                        }}
                        aria-label={
                          formattedPaymentMethod.ariaDescription
                        }
                        id={formattedPaymentMethod.id}
                      />
                    }
                    key={formattedPaymentMethod.paymentMethodId}
                    label={
                      <PaymentMethodDetails
                        paymentMethod={formattedPaymentMethod}
                        showCaIcon={false}
                      />
                    }
                    onClick={() => {
                      onChangeSelectedPaymentMethod(
                        formattedPaymentMethod.id,
                      );
                    }}
                  />
                );
              },
            )}
            <div
              aria-live="polite"
              aria-atomic="true"
              style={{
                position: 'absolute',
                left: '-9999px', // Hide visually but keep it accessible to screen readers
              }}
            >
              {ariaLiveMessage}
            </div>
          </StyledRadioGroup>
        </FormControl>
      </Grid>
      <Grid style={{ padding: '24px 0 0' }}>
        <CallToActionCard
          title="Manage wallet"
          buttonLabel="Manage"
          description="Add, edit, or remove a payment method."
          titleId={TEXT_CONTENT_IDS.CTA_MANAGE_WALLET_TITLE}
          descriptionId={
            TEXT_CONTENT_IDS.CTA_MANAGE_WALLET_DESCRIPTION
          }
          buttonId={ELEMENT_IDS.CTA_MANAGE_WALLET_BUTTON}
          onClick={onManageWalletClick}
        />
      </Grid>
    </Grid>
  );
};
